import { BUILDINGS } from "@zend/content";
import type { BaseState, UnlockView } from "../api.js";
import type { Balances } from "../../state/balances.js";

export type CatalogueGroup = "ready" | "locked" | "built";

/** One catalogue row per grid building type: which group it belongs to and the flags the cards render (D65). */
export interface CatalogueEntry {
  buildingId: string;
  group: CatalogueGroup;
  info: UnlockView;
  /** Every placement cost is covered by the current balances. */
  affordable: boolean;
  /** A free footprint exists (ready types only; built and locked types report true). */
  fits: boolean;
  /** The Querschnitt gate lists the type as unbuilt, so the next HQ level needs it. */
  needed: boolean;
  /** Ready and not in the seen list: became buildable since the catalogue was last opened. */
  isNew: boolean;
  builtRows: { rowId: string; level: number }[];
}

const ORDER: Record<CatalogueGroup, number> = { ready: 0, locked: 1, built: 2 };
const COST_KEYS = ["RAT", "POL", "ALY", "ENC"] as const;

export function buildCatalogue(base: BaseState, balances: Balances | null, seen: readonly string[], fits: (buildingId: string) => boolean): CatalogueEntry[] {
  const needed = new Set(base.gate.drags.filter((d) => d.level === 0).map((d) => d.id));
  const index = new Map(BUILDINGS.map((def, i) => [def.id, i]));
  const out: CatalogueEntry[] = [];
  for (const def of BUILDINGS) {
    if (def.footprint === "ring" || def.id === "bld.command_nexus") continue;
    const info = base.unlocked.find((u) => u.buildingId === def.id);
    if (!info) continue;
    const builtRows = base.buildings.filter((b) => b.buildingId === def.id).map((b) => ({ rowId: b.id, level: b.level }));
    const canPlace = info.unlocked && info.built < info.allowedCopies;
    const group: CatalogueGroup = canPlace ? "ready" : builtRows.length ? "built" : "locked";
    const cost = info.placement.cost;
    const affordable = balances !== null && COST_KEYS.every((k) => (balances[k] ?? 0) >= (cost[k] ?? 0));
    out.push({
      buildingId: def.id,
      group,
      info,
      affordable,
      fits: group === "ready" ? fits(def.id) : true,
      needed: group !== "built" && needed.has(def.id),
      isNew: group === "ready" && !seen.includes(def.id),
      builtRows,
    });
  }
  return out.sort(
    (a, b) =>
      ORDER[a.group] - ORDER[b.group] ||
      Number(b.needed) - Number(a.needed) ||
      (a.group === "locked" ? a.info.unlockHq - b.info.unlockHq : 0) ||
      (index.get(a.buildingId) ?? 0) - (index.get(b.buildingId) ?? 0),
  );
}

const KEY = (accountId: string) => `zend.build.seen:${accountId}`;

/** Types that were already shown as ready; stored per account in the browser only (a convenience, never state). */
export function readSeen(accountId: string): string[] {
  try {
    const raw = localStorage.getItem(KEY(accountId));
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function writeSeen(accountId: string, ids: readonly string[]): void {
  try {
    localStorage.setItem(KEY(accountId), JSON.stringify(ids));
  } catch {
    /* private mode or blocked storage: the ribbon simply shows again next time */
  }
}
